import { COLORS, Typography } from '@musinsa/mds';
import styled from 'styled-components';

import type { GiftDetail } from '@/shared/@types';
import type { FC } from 'react';

type GiftConditionProps = {
  giftDetail: Pick<GiftDetail, 'stock' | 'conditions' | 'startDateTime' | 'endDateTime'>;
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const yy = String(date.getFullYear()).slice(2);
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yy}.${mm}.${dd}`;
};

const getQuantityInfo = (stock: GiftDetail['stock']): string => {
  if (stock.unlimited) return '무제한';
  return `${stock.remainingQuantity.toLocaleString()}개`;
};

const getTargetText = (targetType: string): string => {
  const targetMap: Record<string, string> = {
    SG: '일부상품',
    ALL: '전체상품',
    BRAND: '브랜드 상품',
  };
  return targetMap[targetType] || '일부상품';
};

const getApplicationCondition = (conditions: GiftDetail['conditions']): string => {
  const baseText = '사은품 적용상품';

  if (conditions.giftKind === 'W' && conditions.minimumAmount > 0) {
    return `${baseText} ${conditions.minimumAmount.toLocaleString()}원 이상 구매 시 증정`;
  }

  return `${baseText} 구매 시 증정`;
};

const MAX_DISPLAY_BRANDS = 5;

const getBrandsText = (brands: GiftDetail['conditions']['targetBrands']): string => {
  const displayBrands = brands.slice(0, MAX_DISPLAY_BRANDS).map((brand) => brand.name);
  const remainingCount = brands.length - MAX_DISPLAY_BRANDS;

  if (remainingCount > 0) {
    return `${displayBrands.join(' / ')} 외 ${remainingCount}개`;
  }

  return displayBrands.join(' / ');
};

const GiftCondition: FC<GiftConditionProps> = ({ giftDetail }) => {
  const { stock, conditions, startDateTime, endDateTime } = giftDetail;

  const quantityInfo = getQuantityInfo(stock);
  const startDate = formatDate(startDateTime);
  const endDate = formatDate(endDateTime);
  const applicationCondition = getApplicationCondition(conditions);
  const target = getTargetText(conditions.targetType);
  const brandsText = getBrandsText(conditions.targetBrands);
  const showBrands = conditions.targetType === 'SG';

  return (
    <S.Container>
      <S.SectionTitle>
        <Typography variant="body14pxMed" colorScheme="black">
          사은품 정보
        </Typography>
      </S.SectionTitle>

      <S.InfoList>
        {stock.showRemaining && (
          <S.InfoItem>
            <S.InfoLabel>
              <Typography variant="body13pxReg" colorScheme="black">
                남은 수량
              </Typography>
            </S.InfoLabel>
            <S.InfoValue>
              <Typography variant="body13pxReg" colorScheme="black">
                {quantityInfo}
              </Typography>
            </S.InfoValue>
          </S.InfoItem>
        )}

        <S.InfoItem>
          <S.InfoLabel>
            <Typography variant="body13pxReg" colorScheme="black">
              증정 기간
            </Typography>
          </S.InfoLabel>
          <S.InfoValue>
            <Typography variant="body13pxReg" colorScheme="black">
              {startDate} - {endDate}
            </Typography>
          </S.InfoValue>
        </S.InfoItem>

        <S.InfoItem>
          <S.InfoLabel>
            <Typography variant="body13pxReg" colorScheme="black">
              증정 기준
            </Typography>
          </S.InfoLabel>
          <S.InfoValue>
            <Typography variant="body13pxReg" colorScheme="black">
              {applicationCondition}
            </Typography>
          </S.InfoValue>
        </S.InfoItem>

        <S.InfoItem>
          <S.InfoLabel>
            <Typography variant="body13pxReg" colorScheme="black">
              적용 대상
            </Typography>
          </S.InfoLabel>
          <S.InfoValue>
            <Typography variant="body13pxReg" colorScheme="black">
              {target}
            </Typography>
          </S.InfoValue>
        </S.InfoItem>

        {showBrands && (
          <S.InfoItem>
            <S.InfoLabel>
              <Typography variant="body13pxReg" colorScheme="black">
                적용 브랜드
              </Typography>
            </S.InfoLabel>
            <S.InfoValue>
              <Typography variant="body13pxReg" colorScheme="black">
                {brandsText}
              </Typography>
            </S.InfoValue>
          </S.InfoItem>
        )}
      </S.InfoList>
    </S.Container>
  );
};

const S = {
  Container: styled.section`
    padding: 12px 16px 0 16px;
    background-color: ${COLORS.white};
  `,
  SectionTitle: styled.h2`
    padding-bottom: 4px;
  `,
  InfoList: styled.dl`
    flex-direction: column;
    gap: 8px;
    padding: 4px 0;
    font-size: 13px;
    font-weight: 400;
    line-height: 18px;
    display: flex;
  `,
  InfoItem: styled.div`
    display: flex;
    gap: 16px;

    &:first-of-type {
      padding-top: 0;
    }
  `,
  InfoLabel: styled.dt`
    flex-shrink: 0;
    min-width: 90px;
  `,
  InfoValue: styled.dd`
    flex: 1;
    min-width: 90px;
  `,
};

export { GiftCondition };
